import React from 'react';
import { Box, CircularProgress, Typography, Alert, Button, IconButton, Menu, MenuItem, ListItemText, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Avatar, Divider } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import { hasCoachOversight } from '@rhwb/types';
import { useAuth } from '../contexts/AuthContext';
import { useApp, useOverridePath } from '../contexts/AppContext';
import { getAppConfig } from '../config/appConfig';
import AuthMagicLink from './AuthMagicLink';
import CertificateGenerator from '../CertificateGeneratorSimple';
import CertificateGeneratorS15 from '../CertificateGeneratorS15';
import { callCloudRun } from '../lib/cloudRun';
import { isSurveyPath, recordSurveyLinkOpen } from '../lib/surveyFunnel';
import { useNavigate, useLocation } from 'react-router-dom';
import VeerChatbot from './VeerChatbot';
import HelpCenter from './HelpCenter';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Helper function to get initials from a name
const getInitials = (name: string | null | undefined): string => {
  if (!name) return '?';
  const words = name.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].charAt(0).toUpperCase();
  }
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, logout, session } = useAuth();
  const { selectedRunner, userRole, hybridToggle, setOverrideEmail, setAuthenticatedEmail, overrideEmail, isOverrideActive } = useApp();
  const navigate = useNavigate();
  const withOverride = useOverridePath();
  const location = useLocation();

  const [loadingTimeout, setLoadingTimeout] = React.useState(false);
  const [overrideError, setOverrideError] = React.useState<string | null>(null);
  const [profilePicture, setProfilePicture] = React.useState<string | null>(null);
  const appConfig = getAppConfig();
  
  // Parse email_id from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const emailIdFromUrl = urlParams.get('email_id');
  
  // Track if we've processed the current URL override
  const processedOverrideRef = React.useRef<string | null>(null);

  // Count the shared survey link opened by someone not yet signed in. It
  // identifies only this browser; after sign-in the runner is known by email.
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated && isSurveyPath(location.pathname)) {
      recordSurveyLinkOpen();
    }
  }, [isLoading, isAuthenticated, location.pathname]);

  // Detect timeout scenario: session exists but no user and not loading
  React.useEffect(() => {
    if (session && !user && !isLoading) {
      // This indicates a validation timeout scenario
      // Use a longer timeout (8 seconds) to account for async state updates
      // and give validation time to complete and set user state
      // This is longer than the validation timeout (15s for session restore) to allow for retries
      const timer = setTimeout(() => {
        // Double-check that user is still null before showing timeout
        // This prevents race conditions where user state is being set
        if (session && !user && !isLoading) {
          console.log('[PROTECTED ROUTE] Timeout detected - session exists but no user after 8 seconds');
          setLoadingTimeout(true);
        }
      }, 8000); // Wait 8 seconds to confirm it's stuck (longer than validation timeout + retries)

      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [session, user, isLoading]);

  // Handle email override from URL parameter
  React.useEffect(() => {
    // Only process when user is authenticated
    if (!isAuthenticated || !user) {
      return;
    }

    // Set the authenticated email in AppContext
    setAuthenticatedEmail(user.email);

    // Check if there's an email_id in the URL
    if (emailIdFromUrl) {
      const normalizedOverrideEmail = emailIdFromUrl.toLowerCase().trim();
      
      // Skip if we've already processed this override
      if (processedOverrideRef.current === normalizedOverrideEmail) {
        return;
      }

      process.env.NODE_ENV !== 'production' && console.log('[PROTECTED ROUTE] Email override requested:', normalizedOverrideEmail);
      console.log('[PROTECTED ROUTE] Authenticated user role:', user.role);

      // Check if user has admin/lord role
      if (!hasCoachOversight(user.role)) {
        console.log('[PROTECTED ROUTE] Override rejected - user is not admin/lord');
        setOverrideError('Email override is only allowed for admin or lord users.');
        setOverrideEmail(null);
        processedOverrideRef.current = normalizedOverrideEmail;
        return;
      }

      // Admin user - allow override
      console.log('[PROTECTED ROUTE] Override approved for admin user');
      setOverrideEmail(normalizedOverrideEmail);
      setOverrideError(null);
      processedOverrideRef.current = normalizedOverrideEmail;
    } else {
      // No override in URL - clear any existing override
      if (overrideEmail) {
        console.log('[PROTECTED ROUTE] Clearing email override');
        setOverrideEmail(null);
        processedOverrideRef.current = null;
      }
    }
  }, [isAuthenticated, user, emailIdFromUrl, setAuthenticatedEmail, setOverrideEmail, overrideEmail]);

  // Fetch user's profile picture
  React.useEffect(() => {
    if (!isAuthenticated || !user?.email) {
      setProfilePicture(null);
      return;
    }
    callCloudRun<{ profile: { profile_picture?: string } }>('/api/profile', {})
      .then(res => {
        if (res.profile?.profile_picture) setProfilePicture(res.profile.profile_picture);
      })
      .catch(err => {
        console.error('[PROTECTED ROUTE] Error fetching profile picture:', err);
      });
  }, [isAuthenticated, user?.email, user?.role]);

  // Profile menu state
  const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
  const profileMenuOpen = Boolean(profileMenuAnchor);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };
  
  // Certificate dialog state — Season 14
  const [certificateDialogOpen, setCertificateDialogOpen] = React.useState(false);
  const [runnerData, setRunnerData] = React.useState<any>(null);
  const [loadingRunnerData, setLoadingRunnerData] = React.useState(false);

  // Certificate dialog state — Season 15
  const [certificateS15DialogOpen, setCertificateS15DialogOpen] = React.useState(false);
  const [runnerS15Data, setRunnerS15Data] = React.useState<any>(null);
  const [loadingS15Data, setLoadingS15Data] = React.useState(false);

  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState('');
  const [helpOpen, setHelpOpen] = React.useState(false);
  
  // Determine which email to use for certificate generation.
  // For hybrid users in 'myScore' mode, use their own email (like runners);
  // for hybrid users in 'myCohorts' mode, they need to select a runner (like coaches).
  const isNonRunnerRole = userRole === 'coach' || hasCoachOversight(userRole) || (userRole === 'hybrid' && hybridToggle === 'myCohorts');

  const handleCertificatesClick = () => {
    handleProfileMenuClose();
  };
  const handleSeason14Click = async () => {
    // For coach/admin/hybrid in cohorts mode, check if a runner is selected
    if (isNonRunnerRole && !selectedRunner) {
      setSnackbarMessage('Please select a runner from the dashboard before generating a certificate.');
      setSnackbarOpen(true);
      handleProfileMenuClose();
      return;
    }

    // Use selectedRunner email if available (for coach/admin/hybrid in cohorts mode),
    // otherwise use logged-in user's email (for runner or hybrid in myScore mode)
    const targetEmail = (isNonRunnerRole && selectedRunner) ? selectedRunner : (user?.email || '');
    
    if (!targetEmail) {
      console.error('No email available for certificate generation');
      setSnackbarMessage('Unable to generate certificate: No email address found.');
      setSnackbarOpen(true);
      handleProfileMenuClose();
      return;
    }
    
    setLoadingRunnerData(true);
    try {
      const data = await callCloudRun<{
        runner_name: string | null;
        race_timings: string | null;
        race_distance_completed: string | null;
        coach: string | null;
        race_pr: boolean;
      }>('/api/certificate-data', { email: targetEmail, season: 'Season 14' });

      setRunnerData({
        id: targetEmail,
        name: data.runner_name || targetEmail.split('@')[0] || 'Runner',
        race: data.race_distance_completed || null,
        time: data.race_timings || null,
        coach: data.coach || null,
        race_pr: data.race_pr || false,
        date: new Date().toLocaleDateString(),
      });
      setCertificateDialogOpen(true);
    } catch (error) {
      console.error('Error fetching runner data:', error);
      setRunnerData({
        id: targetEmail,
        name: targetEmail.split('@')[0] || 'Runner',
        race: null,
        time: null,
        coach: null,
        race_pr: false,
        date: new Date().toLocaleDateString(),
      });
      setCertificateDialogOpen(true);
    } finally {
      setLoadingRunnerData(false);
    }
    
    handleProfileMenuClose();
  };
  
  const handleCloseCertificateDialog = () => {
    setCertificateDialogOpen(false);
  };

  const handleSeason15Click = async () => {
    if (isNonRunnerRole && !selectedRunner) {
      setSnackbarMessage('Please select a runner from the dashboard before generating a certificate.');
      setSnackbarOpen(true);
      handleProfileMenuClose();
      return;
    }

    const targetEmail = (isNonRunnerRole && selectedRunner) ? selectedRunner : (user?.email || '');

    if (!targetEmail) {
      setSnackbarMessage('Unable to generate certificate: No email address found.');
      setSnackbarOpen(true);
      handleProfileMenuClose();
      return;
    }

    setLoadingS15Data(true);
    try {
      const data = await callCloudRun<{
        runner_name: string | null;
        race_timings: string | null;
        race_distance_completed: string | null;
        coach: string | null;
        race_pr: boolean;
      }>('/api/certificate-data', { email: targetEmail, season: 'Season 15' });

      setRunnerS15Data({
        id:      targetEmail,
        name:    data.runner_name || targetEmail.split('@')[0] || 'Runner',
        race:    data.race_distance_completed || null,
        time:    data.race_timings || null,
        coach:   data.coach || null,
        race_pr: data.race_pr || false,
      });
      setCertificateS15DialogOpen(true);
    } catch (error) {
      console.error('Error fetching Season 15 runner data:', error);
      setRunnerS15Data({
        id:      targetEmail,
        name:    targetEmail.split('@')[0] || 'Runner',
        race:    null,
        time:    null,
        coach:   null,
        race_pr: false,
      });
      setCertificateS15DialogOpen(true);
    } finally {
      setLoadingS15Data(false);
    }

    handleProfileMenuClose();
  };

  const handleCloseCertificateS15Dialog = () => {
    setCertificateS15DialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };


  // Show loading spinner while checking authentication
  if (isLoading || loadingTimeout) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
          px: 3
        }}
      >
        {loadingTimeout ? (
          <>
            <Alert severity="warning" sx={{ maxWidth: 500 }}>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                Connection Timeout
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                We're having trouble connecting to the authentication server. This could be due to a slow internet connection or temporary server issue.
              </Typography>
            </Alert>
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                background: 'linear-gradient(135deg, #1877F2 0%, #0E5FD3 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0E5FD3 0%, #0A4EB0 100%)',
                }
              }}
            >
              Retry Connection
            </Button>
            <Button
              variant="outlined"
              onClick={logout}
              sx={{ mt: 1 }}
            >
              Sign Out
            </Button>
          </>
        ) : (
          <>
            <CircularProgress size={60} />
            <Typography variant="h6" color="text.secondary">
              Loading...
            </Typography>
          </>
        )}
      </Box>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <AuthMagicLink />;
  }

  // Show user info and logout option
  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* Header with user info and logout */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img 
            src="/rhwb-pulse.ico" 
            alt="RHWB Pulse" 
            style={{ 
              width: 40, 
              height: 40, 
              marginRight: 12,
              borderRadius: '4px'
            }} 
          />
          <Box>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
              {appConfig.appName}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              Welcome, {user?.name || user?.email} ({user?.role})
              {isOverrideActive && (
                <span style={{ color: '#f57c00', fontWeight: 'bold' }}>
                  {' '}(Viewing: {overrideEmail})
                </span>
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Profile Avatar with Menu */}
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              p: 0,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s'
              }
            }}
            aria-label="Profile menu"
          >
            <Avatar
              src={profilePicture || undefined}
              sx={{
                width: 40,
                height: 40,
                bgcolor: profilePicture ? 'transparent' : undefined,
                background: profilePicture ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                fontWeight: 600,
                fontSize: '1rem',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
                }
              }}
            >
              {!profilePicture && getInitials(user?.name || user?.email)}
            </Avatar>
          </IconButton>

          {/* Profile Menu */}
          <Menu
            anchorEl={profileMenuAnchor}
            open={profileMenuOpen}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 200,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: 2,
                border: '1px solid #e0e0e0',
              }
            }}
          >
            {/* Account */}
            <MenuItem
              onClick={() => {
                navigate(withOverride('/profile'));
                handleProfileMenuClose();
              }}
              sx={{ minHeight: 40, gap: 1, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <ListItemText primary="Profile" />
            </MenuItem>
            <Divider />
            {/* App actions */}
            <MenuItem
              onClick={handleCertificatesClick}
              sx={{ minHeight: 40, gap: 1, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <EmojiEventsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <ListItemText primary="Certificates" />
            </MenuItem>
            <MenuItem
              onClick={handleSeason14Click}
              sx={{ minHeight: 40, pl: 5, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <ListItemText primary="Season 14" primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
            <MenuItem
              onClick={handleSeason15Click}
              sx={{ minHeight: 40, pl: 5, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <ListItemText primary="Season 15" primaryTypographyProps={{ variant: 'body2' }} />
            </MenuItem>
            <MenuItem
              onClick={() => { handleProfileMenuClose(); setHelpOpen(true); }}
              sx={{ minHeight: 40, gap: 1, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <HelpOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <ListItemText primary="Help & Support" />
            </MenuItem>
            <Divider />
            {/* Sign out */}
            <MenuItem
              onClick={() => { handleProfileMenuClose(); logout(); }}
              sx={{ minHeight: 40, gap: 1, '&:hover': { bgcolor: '#f5f5f5' } }}
            >
              <LogoutIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <ListItemText primary="Sign Out" />
            </MenuItem>
          </Menu>
        </Box>
      </Box>
      
      {/* Override Error Alert */}
      {overrideError && (
        <Alert 
          severity="error" 
          sx={{ mx: 2, mt: 2 }}
          onClose={() => setOverrideError(null)}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Access Denied
          </Typography>
          <Typography variant="body2">
            {overrideError}
          </Typography>
        </Alert>
      )}

      {/* Override Active Banner */}
      {isOverrideActive && !overrideError && (
        <Alert 
          severity="info" 
          sx={{ mx: 2, mt: 2 }}
        >
          <Typography variant="body2">
            <strong>Override Mode Active:</strong> You are viewing data for <strong>{overrideEmail}</strong>. 
            {' '}
            <Button 
              size="small" 
              variant="text" 
              onClick={() => {
                // Remove email_id from URL and navigate
                const params = new URLSearchParams(location.search);
                params.delete('email_id');
                const newSearch = params.toString();
                navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
              }}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              Exit Override Mode
            </Button>
          </Typography>
        </Alert>
      )}

      {/* Main content */}
      <Box sx={{ p: 2 }}>
        {children}
      </Box>
      
      {/* Certificate Generator Dialog */}
      <Dialog 
        open={certificateDialogOpen} 
        onClose={handleCloseCertificateDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Generate Season 14 Certificate
        </DialogTitle>
        <DialogContent>
          {loadingRunnerData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading runner data...</Typography>
            </Box>
          ) : runnerData ? (
            <CertificateGenerator 
              runner={runnerData}
            />
          ) : (
            <Typography>No runner data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCertificateDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Season 15 Certificate Generator Dialog */}
      <Dialog
        open={certificateS15DialogOpen}
        onClose={handleCloseCertificateS15Dialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Generate Season 15 Certificate
        </DialogTitle>
        <DialogContent>
          {loadingS15Data ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading runner data...</Typography>
            </Box>
          ) : runnerS15Data ? (
            <CertificateGeneratorS15
              runner={runnerS15Data}
            />
          ) : (
            <Typography>No runner data available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCertificateS15Dialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for friendly messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="info" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {location.pathname !== '/veer' && <VeerChatbot />}
      <HelpCenter open={helpOpen} onClose={() => setHelpOpen(false)} />
    </Box>
  );
};

export default ProtectedRoute;
